import { useEffect, useState } from 'react';
import {
  api,
  type AdminOverview,
  type AdminUsers,
  type CalendarSettings,
  type ClientSession,
  type EventHistory,
  type SourceOverview,
} from '../api';
import { Loading } from '../components/Status';
import { DuplicateEventReview } from '../components/DuplicateEventReview';
import './admin-page.css';

export function AdminPage({ session }: { session: ClientSession | null }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [staff, setStaff] = useState<AdminUsers | null>(null);
  const [staffQuery, setStaffQuery] = useState('');
  const [staffRole, setStaffRole] = useState<'admin' | 'editor' | 'viewer' | 'none' | ''>('');
  const [appliedStaffQuery, setAppliedStaffQuery] = useState('');
  const [appliedStaffRole, setAppliedStaffRole] = useState<
    'admin' | 'editor' | 'viewer' | 'none' | ''
  >('');
  const [sources, setSources] = useState<SourceOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const isAdmin = session?.user?.role === 'admin' || !!session?.capabilities.superAdmin;
  const [deleteConfirmation, setDeleteConfirmation] = useState<Record<string, string>>({});
  const [superConfirmation, setSuperConfirmation] = useState<Record<string, string>>({});
  const [historyId, setHistoryId] = useState('');
  const [history, setHistory] = useState<EventHistory | null>(null);
  const [sourceId, setSourceId] = useState('');
  const [sourceCalendarId, setSourceCalendarId] = useState('');
  const [sourceConfiguration, setSourceConfiguration] = useState({
    id: '',
    name: '',
    url: '',
    historyDays: '',
  });
  const [sourceCalendar, setSourceCalendar] = useState<CalendarSettings>({
    name: '',
    description: '',
    color: '#296b62',
    visibility: 'public',
  });

  const loadStaff = async (
    page = staff?.page || 1,
    filters = { query: appliedStaffQuery, role: appliedStaffRole },
  ) => {
    if (!isAdmin) return;
    setStaff(
      await api.adminUsers({
        page,
        pageSize: 25,
        query: filters.query,
        ...(filters.role ? { role: filters.role } : {}),
      }),
    );
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const admin = await api.admin();
      setOverview(admin);
      if (isAdmin) {
        const [users, sourceOverview] = await Promise.allSettled([
          api.adminUsers({ page: 1, pageSize: 25 }),
          api.sources(),
        ]);
        if (users.status === 'fulfilled') setStaff(users.value);
        if (sourceOverview.status === 'fulfilled') setSources(sourceOverview.value);
        const failed = [users, sourceOverview].find((result) => result.status === 'rejected');
        if (failed?.status === 'rejected')
          setError(
            failed.reason instanceof Error
              ? failed.reason.message
              : 'Some administration tools could not load',
          );
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Administration could not load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void load(), []);

  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The change could not be saved');
    } finally {
      setBusy('');
    }
  };

  if (loading)
    return (
      <main id="main-content" className="center-page">
        <Loading label="Loading administration" />
      </main>
    );
  if (!session?.user)
    return (
      <main id="main-content" className="center-page">
        <h1>Sign in to administer calendars</h1>
        <a className="button" href="/login?callbackURL=%2Fadmin">
          Continue to sign in
        </a>
      </main>
    );
  if (!overview)
    return (
      <main id="main-content" className="center-page">
        <h1>Administration unavailable</h1>
        {session?.user && (
          <p>
            <a href="/admin/participation">Attendance &amp; volunteers</a>
          </p>
        )}
        {error && <p className="inline-error">{error}</p>}
      </main>
    );
  const unconfiguredSources =
    sources?.available.filter(
      (available) => !sources.sources.some((source) => source.id === available.id),
    ) ?? [];

  return (
    <main id="main-content" className="settings-page admin-page">
      <a className="back-link" href="/account">
        ← Account
      </a>
      <h1>Administration</h1>
      <a className="button button-quiet" href="/admin/participation">
        Attendance &amp; volunteers
      </a>
      {isAdmin && (
        <nav className="admin-tools" aria-label="Administration tools">
          <a href="/settings" aria-label="Create or edit calendars">
            <strong>Create or edit calendars</strong>
            <span>Manage names, visibility and staff defaults.</span>
          </a>
          <a href="#approve-calendar-source" aria-label="Approve calendar source">
            <strong>Approve calendar source</strong>
            <span>Connect trusted external calendars.</span>
          </a>
          <a href="#calendar-access" aria-label="Calendar access">
            <strong>Calendar access</strong>
            <span>Manage members and invitations.</span>
          </a>
          <a href="/admin/announcements" aria-label="Announcement bar">
            <strong>Announcement bar</strong>
            <span>Schedule a notice for the community.</span>
          </a>
          {session.capabilities.participation && (
            <a href="/participation" aria-label="Polls and feedback">
              <strong>Polls &amp; feedback</strong>
              <span>Create forms, collect votes and review responses.</span>
            </a>
          )}
          {session.capabilities.superAdmin && (
            <>
              <a href="/admin/branding" aria-label="Branding &amp; wording">
                <strong>Branding &amp; wording</strong>
                <span>Preview colors and shared wording.</span>
              </a>
              <a href="/admin/features" aria-label="Features">
                <strong>Features</strong>
                <span>Control feature availability.</span>
              </a>
            </>
          )}
        </nav>
      )}
      <p>Manage staff roles, calendar access, invitations, sources, and event history.</p>
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="inline-success" role="status">
          {notice}
        </p>
      )}

      {staff && (
        <section className="settings-card">
          <h2>Staff grants</h2>
          <p>
            A grant becomes staff access only after a verified Google sign-in from an approved
            domain.
          </p>
          <form
            className="admin-user-filters"
            onSubmit={(event) => {
              event.preventDefault();
              const filters = { query: staffQuery, role: staffRole };
              setAppliedStaffQuery(filters.query);
              setAppliedStaffRole(filters.role);
              void run('staff-search', async () => loadStaff(1, filters));
            }}
          >
            <label className="field">
              <span>Find a user</span>
              <input
                type="search"
                value={staffQuery}
                onChange={(event) => setStaffQuery(event.target.value)}
                placeholder="Name or email"
              />
            </label>
            <label className="field">
              <span>Grant role</span>
              <select
                value={staffRole}
                onChange={(event) => setStaffRole(event.target.value as typeof staffRole)}
              >
                <option value="">All users</option>
                <option value="none">No grant</option>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Administrator</option>
              </select>
            </label>
            <button className="button button-quiet" disabled={Boolean(busy)} type="submit">
              Search users
            </button>
          </form>
          <div className="admin-list">
            {staff.users.map((person) => {
              const isSelf = person.id === session.user?.id;
              const targetRequiresSuperAdmin =
                person.isSuperAdmin && !session.capabilities.superAdmin;
              const roleLabel = person.role
                ? person.role === 'admin'
                  ? 'Administrator'
                  : person.role[0]!.toUpperCase() + person.role.slice(1)
                : person.approvedGoogleIdentity
                  ? 'No staff grant'
                  : 'Not eligible for a staff grant';
              return (
                <div
                  className="admin-row admin-user-row"
                  key={person.id}
                  data-testid={`staff-user-${person.id}`}
                >
                  <span>
                    <b>{person.name}</b>
                    <small>{person.email}</small>
                    <small>
                      {person.isSuperAdmin ? 'Super Admin · ' : ''}
                      {person.accountActive ? 'Account active' : 'Account deactivated'}
                    </small>
                  </span>
                  {isSelf ? (
                    <span className="admin-state admin-self-state">
                      <b>Your account</b>
                      <small>{roleLabel}</small>
                    </span>
                  ) : (
                    <>
                      <div className="admin-staff-controls">
                        <label className="field admin-field">
                          <span className="sr-only">Staff role for {person.name}</span>
                          <select
                            aria-label={`Staff role for ${person.name}`}
                            value={person.role ?? ''}
                            disabled={
                              Boolean(busy) ||
                              targetRequiresSuperAdmin ||
                              !person.accountActive ||
                              !person.approvedGoogleIdentity
                            }
                            onChange={(event) =>
                              void run(`staff-${person.id}`, async () => {
                                await api.updateStaffGrant(
                                  person.id,
                                  event.target.value as 'admin' | 'editor' | 'viewer',
                                  true,
                                  session.csrfToken,
                                );
                                setNotice('Staff grant updated.');
                                await loadStaff();
                              })
                            }
                          >
                            <option value="" disabled>
                              No staff grant
                            </option>
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Administrator</option>
                          </select>
                        </label>
                        {person.role ? (
                          <button
                            className="link-button"
                            type="button"
                            disabled={
                              Boolean(busy) ||
                              targetRequiresSuperAdmin ||
                              !person.accountActive ||
                              (!person.active && !person.approvedGoogleIdentity)
                            }
                            onClick={() =>
                              void run(`staff-status-${person.id}`, async () => {
                                await api.updateStaffGrant(
                                  person.id,
                                  person.role || 'viewer',
                                  !person.active,
                                  session.csrfToken,
                                );
                                setNotice(
                                  person.active
                                    ? 'Staff grant deactivated.'
                                    : 'Staff grant activated.',
                                );
                                await loadStaff();
                              })
                            }
                          >
                            {person.active ? 'Deactivate grant' : 'Activate grant'}
                          </button>
                        ) : (
                          <span className="admin-state">{roleLabel}</span>
                        )}
                      </div>
                      {session.capabilities.superAdmin &&
                        (person.isSuperAdmin || person.role === 'admin') && (
                          <details className="admin-danger-zone">
                            <summary>
                              {person.isSuperAdmin
                                ? 'Remove Super Admin access'
                                : 'Grant Super Admin access'}
                            </summary>
                            <p>
                              Super Admins can manage branding, feature controls and other
                              administrators. This change is audited.
                            </p>
                            <label className="field">
                              <span>Confirm email for {person.name}</span>
                              <input
                                type="email"
                                autoComplete="off"
                                value={superConfirmation[person.id] || ''}
                                onChange={(event) =>
                                  setSuperConfirmation({
                                    ...superConfirmation,
                                    [person.id]: event.target.value,
                                  })
                                }
                              />
                            </label>
                            <button
                              className="button button-small"
                              type="button"
                              disabled={
                                Boolean(busy) ||
                                !person.active ||
                                !person.accountActive ||
                                !person.approvedGoogleIdentity ||
                                superConfirmation[person.id] !== person.email
                              }
                              onClick={() =>
                                void run(`super-admin-${person.id}`, async () => {
                                  await api.updateSuperAdmin(
                                    person.id,
                                    !person.isSuperAdmin,
                                    !!person.isSuperAdmin,
                                    superConfirmation[person.id] || '',
                                    session.csrfToken,
                                  );
                                  setSuperConfirmation({ ...superConfirmation, [person.id]: '' });
                                  setNotice(
                                    person.isSuperAdmin
                                      ? 'Super Admin access removed.'
                                      : 'Super Admin access granted.',
                                  );
                                  await loadStaff();
                                })
                              }
                            >
                              {person.isSuperAdmin
                                ? 'Remove Super Admin access'
                                : 'Grant Super Admin access'}
                            </button>
                          </details>
                        )}
                      <div className="admin-account-controls">
                        <button
                          className="link-button"
                          type="button"
                          disabled={Boolean(busy) || targetRequiresSuperAdmin}
                          aria-label={`${person.accountActive ? 'Deactivate' : 'Activate'} account for ${person.name}`}
                          onClick={() =>
                            void run(`account-status-${person.id}`, async () => {
                              await api.updateAdminUser(
                                person.id,
                                !person.accountActive,
                                session.csrfToken,
                              );
                              setNotice(
                                person.accountActive
                                  ? 'Account deactivated.'
                                  : 'Account activated.',
                              );
                              await loadStaff();
                            })
                          }
                        >
                          {person.accountActive ? 'Deactivate account' : 'Activate account'}
                        </button>
                        {session.capabilities.superAdmin && (
                          <details className="admin-danger-zone">
                            <summary>Delete account</summary>
                            <p>
                              Permanently delete this account and its access. Type the email address
                              to confirm.
                            </p>
                            <label className="field">
                              <span>Type {person.email} to confirm</span>
                              <input
                                type="email"
                                value={deleteConfirmation[person.id] || ''}
                                onChange={(event) =>
                                  setDeleteConfirmation({
                                    ...deleteConfirmation,
                                    [person.id]: event.target.value,
                                  })
                                }
                              />
                            </label>
                            <button
                              className="button admin-delete-button"
                              type="button"
                              disabled={
                                Boolean(busy) || deleteConfirmation[person.id] !== person.email
                              }
                              onClick={() =>
                                void run(`account-delete-${person.id}`, async () => {
                                  await api.deleteAdminUser(
                                    person.id,
                                    deleteConfirmation[person.id] || '',
                                    session.csrfToken,
                                  );
                                  setDeleteConfirmation((current) => {
                                    const next = { ...current };
                                    delete next[person.id];
                                    return next;
                                  });
                                  setNotice('Account deleted.');
                                  await loadStaff();
                                })
                              }
                            >
                              Permanently delete {person.name}
                            </button>
                          </details>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {!staff.users.length && <p className="admin-empty">No users match these filters.</p>}
          <div className="admin-pagination" aria-label="Staff user pages">
            <span>
              {staff.total} user{staff.total === 1 ? '' : 's'}
            </span>
            <button
              className="link-button"
              disabled={Boolean(busy) || staff.page <= 1}
              onClick={() => void run('staff-previous', async () => loadStaff(staff.page - 1))}
            >
              Previous
            </button>
            <span>
              Page {staff.page} of {staff.totalPages}
            </span>
            <button
              className="link-button"
              disabled={Boolean(busy) || staff.page >= staff.totalPages}
              onClick={() => void run('staff-next', async () => loadStaff(staff.page + 1))}
            >
              Next
            </button>
          </div>
        </section>
      )}

      <section className="settings-card" id="calendar-access">
        <h2>Calendar access</h2>
        <p>Open a calendar to manage its members and invitations.</p>
        <div className="admin-list">
          {overview.calendars.map((calendar) => {
            const memberCount = overview.memberships.filter(
              (membership) => membership.calendar_id === calendar.id,
            ).length;
            const pendingCount = overview.invitations.filter(
              (invitation) =>
                invitation.calendar_id === calendar.id &&
                !invitation.accepted_at &&
                !invitation.revoked_at,
            ).length;
            return (
              <div className="admin-row" key={calendar.id}>
                <span>
                  <b>{calendar.name}</b>
                  <small>
                    {memberCount} member{memberCount === 1 ? '' : 's'} · {pendingCount} pending
                  </small>
                </span>
                <a
                  className="button button-quiet"
                  href={`/admin/calendars/${encodeURIComponent(calendar.id)}/members`}
                  aria-label={`Manage ${calendar.name} access`}
                >
                  Manage access
                </a>
              </div>
            );
          })}
        </div>
        {!overview.calendars.length && <p className="admin-empty">No calendars available.</p>}
      </section>

      {sources && (
        <section className="settings-card">
          <h2>Approved calendar sources</h2>
          <p>
            Approve a Google or Microsoft calendar feed, then connect it to a calendar. Other
            providers need a reviewed connector before they can be added.
          </p>
          <details className="admin-source-setup" id="approve-calendar-source" open>
            <summary>Approve calendar source</summary>
            <div className="form-grid">
              <label className="field">
                <span>Source ID</span>
                <input
                  value={sourceConfiguration.id}
                  onChange={(event) =>
                    setSourceConfiguration({ ...sourceConfiguration, id: event.target.value })
                  }
                  placeholder="district-calendar"
                />
              </label>
              <label className="field">
                <span>Source name</span>
                <input
                  value={sourceConfiguration.name}
                  onChange={(event) =>
                    setSourceConfiguration({ ...sourceConfiguration, name: event.target.value })
                  }
                  placeholder="District calendar"
                />
              </label>
              <label className="field admin-source-url">
                <span>Google or Microsoft calendar URL</span>
                <input
                  type="url"
                  value={sourceConfiguration.url}
                  onChange={(event) =>
                    setSourceConfiguration({ ...sourceConfiguration, url: event.target.value })
                  }
                  placeholder="https://calendar.google.com/..."
                />
              </label>
              <label className="field">
                <span>History window (days)</span>
                <input
                  type="number"
                  min={1}
                  max={3650}
                  value={sourceConfiguration.historyDays}
                  onChange={(event) =>
                    setSourceConfiguration({
                      ...sourceConfiguration,
                      historyDays: event.target.value,
                    })
                  }
                  placeholder="365"
                />
              </label>
              <button
                className="button"
                disabled={
                  Boolean(busy) ||
                  !sourceConfiguration.id ||
                  !sourceConfiguration.name.trim() ||
                  !sourceConfiguration.url ||
                  (sourceConfiguration.historyDays !== '' &&
                    !/^[1-9]\d{0,3}$/.test(sourceConfiguration.historyDays))
                }
                onClick={() =>
                  void run('source-approval', async () => {
                    const { historyDays, ...configuration } = sourceConfiguration;
                    const created = await api.createSourceConfiguration(
                      historyDays
                        ? { ...configuration, historyDays: Number(historyDays) }
                        : configuration,
                      session.csrfToken,
                    );
                    setSourceId(created.source.id);
                    setSourceConfiguration({ id: '', name: '', url: '', historyDays: '' });
                    setSources(await api.sources());
                    setNotice('Calendar source approved. Create a target calendar to connect it.');
                  })
                }
              >
                Approve source
              </button>
            </div>
          </details>
          {unconfiguredSources.length > 0 && (
            <details className="admin-source-setup">
              <summary>Set up a new source calendar</summary>
              <div className="form-grid">
                <label className="field">
                  <span>Approved source</span>
                  <select
                    value={sourceId}
                    disabled={Boolean(sourceCalendarId)}
                    onChange={(event) => setSourceId(event.target.value)}
                  >
                    <option value="">Choose a source</option>
                    {unconfiguredSources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>New calendar name</span>
                  <input
                    value={sourceCalendar.name}
                    disabled={Boolean(sourceCalendarId)}
                    onChange={(event) =>
                      setSourceCalendar({ ...sourceCalendar, name: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Description</span>
                  <input
                    value={sourceCalendar.description || ''}
                    disabled={Boolean(sourceCalendarId)}
                    onChange={(event) =>
                      setSourceCalendar({ ...sourceCalendar, description: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Visibility</span>
                  <select
                    value={sourceCalendar.visibility}
                    disabled={Boolean(sourceCalendarId)}
                    onChange={(event) =>
                      setSourceCalendar({
                        ...sourceCalendar,
                        visibility: event.target.value as 'public' | 'private',
                      })
                    }
                  >
                    <option value="public">Public</option>
                    <option value="private">Members only</option>
                  </select>
                </label>
                <button
                  className="button"
                  disabled={Boolean(busy) || !sourceId || !sourceCalendar.name.trim()}
                  onClick={() =>
                    void run('source-setup', async () => {
                      let calendarId = sourceCalendarId;
                      if (!calendarId) {
                        const calendar = await api.createCalendar(
                          sourceCalendar,
                          session.csrfToken,
                        );
                        calendarId = calendar.calendar.id;
                        setSourceCalendarId(calendarId);
                      }
                      try {
                        await api.updateSource(sourceId, calendarId, true, session.csrfToken);
                      } catch (cause) {
                        setNotice(
                          'Calendar created. Choose the approved source again to retry connecting it.',
                        );
                        throw cause;
                      }
                      setSourceCalendar({
                        name: '',
                        description: '',
                        color: '#296b62',
                        visibility: 'public',
                      });
                      setSourceCalendarId('');
                      setSourceId('');
                      setNotice('Source calendar created and connected.');
                      await load();
                    })
                  }
                >
                  {sourceCalendarId ? 'Connect source' : 'Create and connect source'}
                </button>
              </div>
            </details>
          )}
          {sources.available.length ? (
            sources.available.map((available) => {
              const configured = sources.sources.find((source) => source.id === available.id);
              return (
                <div className="admin-row" key={available.id}>
                  <span>
                    <b>{available.name}</b>
                    <small>
                      {configured?.error || configured?.lastSuccessAt || 'Not configured'}
                    </small>
                    {configured?.summary && <small>{configured.summary}</small>}
                  </span>
                  <label className="field admin-field">
                    <span className="sr-only">Target calendar for {available.name}</span>
                    <select
                      aria-label={`Target calendar for ${available.name}`}
                      value={configured?.calendarId || overview.calendars[0]?.id || ''}
                      disabled={Boolean(configured)}
                    >
                      {overview.calendars.map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="button button-quiet"
                    disabled={Boolean(busy) || !overview.calendars.length}
                    onClick={(event) => {
                      const row = event.currentTarget.parentElement;
                      const calendarId =
                        row?.querySelector('select')?.value || configured?.calendarId;
                      if (!calendarId) return;
                      void run(`source-${available.id}`, async () => {
                        await api.updateSource(
                          available.id,
                          calendarId,
                          !configured?.enabled,
                          session.csrfToken,
                        );
                        setNotice(configured?.enabled ? 'Source disabled.' : 'Source enabled.');
                        setSources(await api.sources());
                      });
                    }}
                  >
                    {configured?.enabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              );
            })
          ) : (
            <p>No source names are approved in this deployment.</p>
          )}
        </section>
      )}

      {isAdmin && (
        <DuplicateEventReview
          csrfToken={session.csrfToken}
          onNotice={setNotice}
          onError={setError}
        />
      )}

      <section className="settings-card">
        <h2>Event history</h2>
        <div className="admin-history-search">
          <label className="field">
            <span>Event ID</span>
            <input value={historyId} onChange={(event) => setHistoryId(event.target.value)} />
          </label>
          <button
            className="button button-quiet"
            disabled={Boolean(busy) || !historyId}
            onClick={() =>
              void run('history', async () => setHistory(await api.eventHistory(historyId)))
            }
          >
            View history
          </button>
        </div>
        {history?.history.map((entry) => (
          <div className="admin-history-entry" key={entry.version}>
            <b>Version {entry.version}</b> by {entry.actorName} on{' '}
            {new Date(entry.createdAt).toLocaleString()}
            <small>
              {entry.changes.length ? `Changed: ${entry.changes.join(', ')}` : 'Recorded version'}
            </small>
          </div>
        ))}
      </section>
    </main>
  );
}
